import React, { useContext, useState, useCallback, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ApplicationContext } from "../../context/GlobalState";
import { useLocale } from "../../context/LocaleContext";
import { t, SUPPORTED_LOCALES, extractLocaleFromPath } from "../../i18n";
import { getLocalizedDisplay, getLocalizedField } from "../../utils/localizedDisplay";
import { brandLocations as resolveBrandLocations, locationFromPath } from "../../utils/locationResolver";
import LocationSelector, { LOCATION_PICKER_EVENTS } from "../LocationSelector/LocationSelector";
import { FaBars, FaTimes, FaPhone, FaGlobe, FaChevronDown, FaMapMarkerAlt } from "react-icons/fa";
import "./Header.css";

/* Hover-intent hook: opens immediately, closes after a delay so user can cross the gap.
   Also exposes toggle/close for click + keyboard control (WCAG 2.1.1 — 2026-07-21 audit H1). */
function useHoverIntent(closeDelay = 300) {
  const [open, setOpen] = useState(false);
  const timeout = useRef(null);
  const onEnter = useCallback(() => { clearTimeout(timeout.current); setOpen(true); }, []);
  const onLeave = useCallback(() => { timeout.current = setTimeout(() => setOpen(false), closeDelay); }, [closeDelay]);
  const toggle = useCallback(() => { clearTimeout(timeout.current); setOpen(o => !o); }, []);
  const close = useCallback(() => { clearTimeout(timeout.current); setOpen(false); }, []);
  return { open, onEnter, onLeave, toggle, close };
}

// Use public/ paths so logos are never bundled (avoids 404 on missing src/assets)
const BRAND_LOGOS = {
  gso: "/assets/brand/gso/logo-color.webp",
  "apex-nc": "/assets/brand/apex-nc/logo-horizontal.webp",
  "apex-tn": "/assets/brand/apex-tn/logo-horizontal.webp",
};

export default function Header() {
  const { state, dispatch } = useContext(ApplicationContext);
  const location = useLocation();
  const navigate = useNavigate();
  const { locale, setLocale } = useLocale();
  const [mobileExpanded, setMobileExpanded] = useState({});
  const [langOpen, setLangOpen] = useState(false);

  /* Hover-intent for each desktop dropdown */
  const servicesHover = useHoverIntent(280);
  const resourcesHover = useHoverIntent(280);
  const languageHover = useHoverIntent(280);

  const brand = state.brand;
  const brandKey = brand.brandKey || 'gso';
  const services = state.taxonomy?.dimensions?.find((d) => d.id === "service")?.values || [];

  // Filter locations to current brand. If the brand declares physicalSiteSlugs
  // (TMM ground-truth physical sites), restrict the visible navigation surface
  // to that set — long-tail city routes remain reachable by direct URL.
  // The filter itself now lives in utils/locationResolver so the Header, the
  // header LocationSelector and the first-visit LocationGate cannot drift.
  const brandLocations = resolveBrandLocations(brand, state.taxonomy);

  // Context-aware service links. Precedence: location in the URL (authoritative)
  // → the visitor's persisted/selected clinic → UNKNOWN.
  // The old code fell back to `brandLocations[0]?.slug || 'atlanta'`, so a
  // visitor in Raleigh got Atlanta service links out of the mega-menu. When the
  // clinic is unknown we now surface the location CTA instead of silently
  // linking a foreign city.
  const resolvedLocationSlug =
    locationFromPath(location.pathname, brandLocations) || state.selectedLocationSlug || null;
  const locationUnknown = !resolvedLocationSlug;
  // Only used for active-route highlighting, never for building service links.
  const currentLocationSlug = resolvedLocationSlug || brandLocations[0]?.slug || 'atlanta';

  // Group services by category
  const serviceCategories = state.taxonomy?.serviceCategories || [];

  const switchLanguage = (newLocale) => {
    setLocale(newLocale);
    setLangOpen(false);
    // Navigate to the same page with the new locale prefix
    const { pathWithoutLocale } = extractLocaleFromPath(location.pathname);
    const newPrefix = newLocale === 'en' ? '' : `/${newLocale}`;
    const newPath = newPrefix + (pathWithoutLocale === '/' ? '/' : pathWithoutLocale);
    navigate(newPath);
  };

  const closeMobile = useCallback(() => {
    dispatch({ type: "SET_MOBILE_MENU", payload: false });
  }, [dispatch]);

  const toggleMobileSection = (section) => {
    setMobileExpanded((prev) => ({ ...prev, [section]: !prev[section] }));
  };

  const handleHamburgerClick = (e) => {
    e.stopPropagation();
    dispatch({ type: "TOGGLE_MOBILE_MENU" });
  };

  const prefix = locale === 'en' ? '' : `/${locale}`;

  // Active route detection for neumorphic persistent state
  const path = location.pathname;
  const isActive = (route) => {
    const full = `${prefix}${route}`;
    return path === full || path === `${full}/`;
  };
  const isActivePrefix = (routePrefix) => {
    const full = `${prefix}${routePrefix}`;
    return path.startsWith(full);
  };

  return (
    <>
      <header className="site-header">
        <div className="header-container">
          <Link to={`${prefix}/`} className="header-logo">
            <img
              src={BRAND_LOGOS[brand.brandKey] || BRAND_LOGOS['gso']}
              alt={`${brand.name} logo`}
              className="header-logo-img"
              width="160"
              height="48"
              onError={(e) => {
                e.target.style.display = 'none';
                const fallback = e.target.nextSibling;
                if (fallback) fallback.style.display = 'block';
              }}
            />
            <span className="header-logo-text" style={{ display: 'none' }}>{brand.name}</span>
          </Link>

          <nav className="header-nav-desktop">
            <div className="nav-dropdown" onMouseEnter={servicesHover.onEnter} onMouseLeave={servicesHover.onLeave} onKeyDown={(e) => { if (e.key === 'Escape') servicesHover.close(); }}>
              <button type="button" className={`nav-link neu-link nav-drop-trigger${isActivePrefix(`/${currentLocationSlug}/`) ? ' active' : ''}`} aria-expanded={servicesHover.open} aria-haspopup="true" onClick={servicesHover.toggle}>{t("nav.services", locale)}</button>
              <div className={`nav-dropdown-menu mega-menu${servicesHover.open ? ' js-open' : ''}`}>
                {/* Clinic-unknown banner: a slim prompt in the footer, NOT a
                    replacement for the menu — the full services grid stays
                    visible so visitors always see what we offer. */}
                <div className="mega-menu-grid mega-menu-grid--five">
                  {(() => {
                    // Group the sparse categories into shared stacked columns so
                    // the menu stays 5 columns wide and never bleeds off the
                    // right edge (owner 2026-10-13). Tall/dense categories keep
                    // their own column; the short ones stack vertically.
                    const catById = {};
                    serviceCategories.forEach((c) => { catById[c.id] = c; });
                    const byId = (id) => catById[id];
                    const COLUMNS = [
                      // single-category columns (dense):
                      ['accident-recovery'],
                      ['surgical'],
                      ['treatment'],
                      // stacked column of short categories:
                      ['injury-legal', 'legal-support', 'rehabilitation'],
                      // stacked column of short categories:
                      ['diagnostic', 'general', 'condition'],
                    ];
                    // Build from the categories that actually exist for this
                    // brand (fall back to one group per category if none match).
                    const groups = COLUMNS
                      .map((ids) => ids.map(byId).filter(Boolean))
                      .filter((g) => g.length > 0);
                    const usable = groups.length > 0 ? groups : serviceCategories.map((c) => [c]);
                    return usable.map((columnCats, ci) => (
                      <div key={ci} className="mega-menu-column">
                        {columnCats.map((cat) => (
                          <div key={cat.id} className="nav-dropdown-group">
                            <span className="nav-dropdown-label">{getLocalizedField(cat, 'label', locale)}</span>
                            {services.filter((s) => s.category === cat.id).map((s) => (
                              <Link key={s.slug} to={`${prefix}/${currentLocationSlug}/${s.slug}/`} className="nav-dropdown-item">
                                {getLocalizedDisplay(s, locale)}
                              </Link>
                            ))}
                          </div>
                        ))}
                      </div>
                    ));
                  })()}
                </div>
                {/* Footer promo band: Schedule-Appointment CTA (GA4 key event via
                    data-pseo-cta — no new JS) + clinic prompt when unknown. */}
                <div className="mega-menu-footer">
                  <div className="mega-menu-footer-cta">
                    <Link
                      to={`${prefix}/request-appt/`}
                      className="mega-menu-footer-btn"
                      data-pseo-cta="nav_schedule_appointment"
                      data-pseo-cta-text="Schedule an appointment"
                    >
                      {t('cta.schedule', locale)}
                    </Link>
                    <span className="mega-menu-footer-hint">{t('cta.scheduleHint', locale)}</span>
                  </div>
                  {locationUnknown ? (
                    <button
                      type="button"
                      className="nav-loc-cta mega-menu-footer-loc"
                      onClick={() => { servicesHover.close(); window.dispatchEvent(new Event(LOCATION_PICKER_EVENTS.desktop)); }}
                    >
                      <FaMapMarkerAlt /> {t('locationGate.chooseLocation', locale)}
                    </button>
                  ) : (
                    <Link to={`${prefix}/services/`} className="mega-menu-footer-loc-link">{t('cta.viewAllServices', locale)}</Link>
                  )}
                </div>
              </div>
            </div>

            {/* LEGACY LOCATIONS NAV ITEM FULLY REMOVED FROM THE PRIMARY NAV
                BAR (2026-08-12). The header "Your Clinic" LocationSelector is
                the single location control; a second top-nav "Locations" item
                showed visitors two competing pickers. The /locations/ hub is
                NOT orphaned — it is linked as a discreet item inside the
                Resources dropdown (below) and from the footer. Choosing a
                clinic is the selector's job. */}

            <Link to={`${prefix}/meet-our-team/`} className={`nav-link neu-link${isActivePrefix('/meet-our-team') ? ' active' : ''}`}>{t("nav.team", locale)}</Link>
            <Link to={`${prefix}/injuries/`} className={`nav-link neu-link${isActivePrefix('/injuries') ? ' active' : ''}`}>{t("nav.injuries", locale)}</Link>

            <div className="nav-dropdown" onMouseEnter={resourcesHover.onEnter} onMouseLeave={resourcesHover.onLeave} onKeyDown={(e) => { if (e.key === 'Escape') resourcesHover.close(); }}>
              <button type="button" className={`nav-link neu-link nav-drop-trigger${isActivePrefix('/blog') || isActivePrefix('/faqs') || isActivePrefix('/core-exercises') || isActivePrefix('/media-center') || isActivePrefix('/resources') ? ' active' : ''}`} aria-expanded={resourcesHover.open} aria-haspopup="true" onClick={resourcesHover.toggle}>{t("nav.resources", locale)}</button>
              <div className={`nav-dropdown-menu resources-dropdown-menu${resourcesHover.open ? ' js-open' : ''}`} style={{ minWidth: 200 }}>
                <Link to={`${prefix}/blog/`} className="nav-dropdown-item">📝 Blog</Link>
                <Link to={`${prefix}/faqs/`} className="nav-dropdown-item">❓ FAQs</Link>
                <Link to={`${prefix}/core-exercises/`} className="nav-dropdown-item">🏋️ {t('resources.coreExercises', locale)}</Link>
                <Link to={`${prefix}/media-center/`} className="nav-dropdown-item">🎬 {t('resources.mediaCenter', locale)}</Link>
                <Link to={`${prefix}/resources/patient-referral/`} data-pseo-attorney data-pseo-cta="nav_patient_referral" className="nav-dropdown-item">⚖️ {t('resources.patientReferral', locale)}</Link>
                <Link to={`${prefix}/locations/`} className="nav-dropdown-item">📍 {t("nav.locations", locale)}</Link>
                {(brandKey === 'apex-nc' || brandKey === 'apex-tn' || brandKey === 'gso') && (
                  <Link to={`${prefix}/new-patient-forms/`} className="nav-dropdown-item">📄 {t('resources.patientForms', locale)}</Link>
                )}
              </div>
            </div>

            <Link to={`${prefix}/contact/`} className={`nav-link neu-link${isActive('/contact') ? ' active' : ''}`}>{t("nav.contact", locale)}</Link>
          </nav>

          <div className="header-actions">
            {/* Prominent clinic selector — primary chrome, not a buried nav item */}
            <LocationSelector variant="desktop" openEvent={LOCATION_PICKER_EVENTS.desktop} />
            {/* Language Switcher — hover-intent like all nav dropdowns */}
            <div className="nav-dropdown language-switcher" onMouseEnter={languageHover.onEnter} onMouseLeave={languageHover.onLeave} onKeyDown={(e) => { if (e.key === 'Escape') languageHover.close(); }} style={{ position: 'relative' }}>
              <button type="button" className="language-toggle" aria-expanded={languageHover.open} aria-haspopup="true" onClick={languageHover.toggle}>
                <FaGlobe /> {SUPPORTED_LOCALES.find(l => l.code === locale)?.label || 'English'}
              </button>
              <div className={`language-dropdown nav-dropdown-menu${languageHover.open ? ' js-open' : ''}`}>
                {SUPPORTED_LOCALES.map(l => (
                  <button key={l.code} className={`lang-option ${l.code === locale ? 'active' : ''}`} onClick={() => switchLanguage(l.code)}>
                    {l.label}
                  </button>
                ))}
              </div>
            </div>
            <a href={`tel:${brand.phone}`} className="header-cta-phone">
              <FaPhone /> {brand.phoneExisting ? `New: ${brand.phone}` : brand.phone}
            </a>
            {brand.phoneExisting && (
              <a href={`tel:${brand.phoneExisting}`} className="header-cta-phone header-cta-phone-existing">
                Existing: {brand.phoneExisting}
              </a>
            )}
            <Link to={`${prefix}/request-appt/`} className="header-cta-btn">
              {t("cta.schedule", locale)}
            </Link>
          </div>

          {/* Mobile: the selector lives in the header BAR (not inside the
              hamburger) so a phone visitor can always set their clinic. */}
          <LocationSelector variant="mobile" openEvent={LOCATION_PICKER_EVENTS.mobile} />

          <button className={`hamburger-menu ${state.isMobileMenuOpen ? "active" : ""}`} onClick={handleHamburgerClick} aria-label={t('nav.toggleMenu', locale)}>
            {state.isMobileMenuOpen ? <FaTimes /> : <FaBars />}
          </button>
        </div>
      </header>

      <div className={`mobile-backdrop ${state.isMobileMenuOpen ? "open" : ""}`} onClick={closeMobile} />

      <nav className={`mobile-sidebar ${state.isMobileMenuOpen ? "open" : ""}`}>
        <div className="mobile-sidebar-header">
          <button className="mobile-close-btn" onClick={closeMobile} aria-label={t('nav.closeMenu', locale)}><FaTimes /></button>
          <h2>{brand.shortName || brand.name}</h2>
        </div>
        <div className="mobile-sidebar-links">
          <Link to={`${prefix}/`} onClick={closeMobile}>{t("nav.home", locale)}</Link>

          <button className={`mobile-accordion-toggle ${mobileExpanded.services ? "expanded" : ""}`} aria-expanded={!!mobileExpanded.services} onClick={() => toggleMobileSection("services")}>
            {t("nav.services", locale)} <FaChevronDown className="mobile-chevron" />
          </button>
          {mobileExpanded.services && (
            <div className="mobile-accordion-content">
              {locationUnknown ? (
                <button
                  type="button"
                  className="nav-loc-cta"
                  onClick={() => { closeMobile(); window.dispatchEvent(new Event(LOCATION_PICKER_EVENTS.mobile)); }}
                >
                  {t('locationGate.chooseLocation', locale)}
                </button>
              ) : services.map((s) => (
                <Link key={s.slug} to={`${prefix}/${currentLocationSlug}/${s.slug}/`} onClick={closeMobile}>{getLocalizedDisplay(s, locale)}</Link>
              ))}
            </div>
          )}

          {/* Legacy per-city accordion + standalone Locations link removed
              (2026-08-12) — the header "Your Clinic" LocationSelector pill
              (outside the hamburger) is the single location control on mobile
              too. The /locations/ directory hub is reachable from the footer. */}

          <Link to={`${prefix}/meet-our-team/`} onClick={closeMobile}>{t("nav.team", locale)}</Link>
          <Link to={`${prefix}/injuries/`} onClick={closeMobile}>{t("nav.injuries", locale)}</Link>

          <button className={`mobile-accordion-toggle ${mobileExpanded.resources ? "expanded" : ""}`} aria-expanded={!!mobileExpanded.resources} onClick={() => toggleMobileSection("resources")}>
            {t("nav.resources", locale)} <FaChevronDown className="mobile-chevron" />
          </button>
          {mobileExpanded.resources && (
            <div className="mobile-accordion-content">
              <Link to={`${prefix}/blog/`} onClick={closeMobile}>📝 Blog</Link>
              <Link to={`${prefix}/faqs/`} onClick={closeMobile}>❓ FAQs</Link>
              <Link to={`${prefix}/core-exercises/`} onClick={closeMobile}>🏋️ {t('resources.coreExercises', locale)}</Link>
              <Link to={`${prefix}/media-center/`} onClick={closeMobile}>🎬 {t('resources.mediaCenter', locale)}</Link>
              <Link to={`${prefix}/resources/patient-referral/`} data-pseo-attorney data-pseo-cta="nav_patient_referral_m" onClick={closeMobile}>⚖️ {t('resources.patientReferral', locale)}</Link>
              {(brandKey === 'apex-nc' || brandKey === 'apex-tn' || brandKey === 'gso') && (
                <Link to={`${prefix}/new-patient-forms/`} onClick={closeMobile}>📄 {t('resources.patientForms', locale)}</Link>
              )}
            </div>
          )}
          <Link to={`${prefix}/contact/`} onClick={closeMobile}>{t("nav.contact", locale)}</Link>

          <hr />
          <a href={`tel:${brand.phone}`} className="mobile-cta-phone"><FaPhone /> {brand.phoneExisting ? `New: ${brand.phone}` : brand.phone}</a>
          {brand.phoneExisting && (
            <a href={`tel:${brand.phoneExisting}`} className="mobile-cta-phone mobile-cta-phone-existing">Existing: {brand.phoneExisting}</a>
          )}
          <Link to={`${prefix}/request-appt/`} className="mobile-cta-appt" onClick={closeMobile}>{t("cta.schedule", locale)}</Link>
          
          <div className="mobile-lang-list">
            {SUPPORTED_LOCALES.map(l => (
              <button key={l.code} className={`lang-option ${l.code === locale ? 'active' : ''}`} onClick={() => { switchLanguage(l.code); closeMobile(); }}>
                {l.label}
              </button>
            ))}
          </div>
        </div>
      </nav>
    </>
  );
}
